import Button from 'components/UI/Button';
import Card from 'components/UI/Card';
import CardHeader from 'components/UI/Card/CardHeader';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import Loader from 'components/UI/Loader';
import PageWrapper from 'components/UI/PageWrapper';
import Pagination, { PagePagination } from 'components/UI/Pagination';
import SearchField from 'components/UI/SearchField';
import ConfigTable from 'components/husqiotBus/ConfigTable';
import SubscriptionCard from 'components/husqiotBus/SubscriptionCard';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  ConfigFilter,
  ConfigFilterAttribute,
  HusqiotbusConfigList,
} from 'models/husqiotbus';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import DIRECTORY, {
  saveGeneratedXml,
} from 'services/husqiotBusService/fileService';
import { generateXmlFromTemplates } from 'services/husqiotBusService/husqiotBusService';
import {
  configListSelector,
  fetchBasicData,
  fetchConfigList,
  resetSelectedService,
} from 'store/husqiotbusState/husqiotbusState';
import { ButtonGrid } from 'styles';
import { showErrorToast } from 'utils/toast';

// Initial pagination
const initialPagination = {
  limit: 10,
  offset: 0,
  page: 1,
};

const HusqiotbusStartPage: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const {
    data: services,
    isLoading,
    filters,
    totalCount,
  }: HusqiotbusConfigList = useAppSelector(configListSelector);

  const [pagination, setPagination] =
    useState<PagePagination>(initialPagination);

  // Mount
  useEffect(() => {
    if (DIRECTORY.directoryHandle) {
      dispatch(fetchConfigList(0));
      dispatch(fetchBasicData());
    }
  }, [dispatch]);

  // Fetch devices
  const fetchConfigs = useCallback(
    (configFilter: ConfigFilter, offset?: number) => {
      dispatch(fetchConfigList(offset ?? 0, configFilter));
    },
    [dispatch],
  );

  // Fetch basic data
  const fetchBasicDataDisp = useCallback(() => {
    dispatch(fetchBasicData());
  }, [dispatch]);

  // Search
  const onSearch = useCallback(
    (value: string) => {
      const searchFilters = {
        attribute: ConfigFilterAttribute.name,
        value,
      };
      setPagination(initialPagination);
      fetchConfigs(searchFilters, initialPagination.offset);
    },
    [fetchConfigs],
  );

  const onGenerateXml = useCallback(async () => {
    if (!DIRECTORY.directoryHandle) {
      DIRECTORY.directoryHandle = await (window as any).showDirectoryPicker({
        id: 'pathToBus',
      });
    }
    const xml = await generateXmlFromTemplates();
    await saveGeneratedXml(xml);
  }, []);

  // Paginate
  const onSetPagination = useCallback(
    (paginate: PagePagination) => {
      setPagination(paginate);
      fetchConfigs(filters.data || [], paginate.offset);
    },
    [fetchConfigs, filters.data],
  );

  const openDirectory = useCallback(async () => {
    try {
      const directoryHandle = await (window as any).showDirectoryPicker({
        id: 'pathToBus',
      });
      if (directoryHandle.name !== 'husqiot-bus') {
        throw new Error('Invalid directory, have to be husqiot-bus');
      }
      DIRECTORY.directoryHandle = directoryHandle;
      fetchConfigs(filters.data, 0);
      fetchBasicDataDisp();
    } catch (e: any) {
      console.error('Error opening directory', e);
      showErrorToast(intl.formatMessage({ id: 'husqiotbus.folder_error' }));
    }
  }, [intl, fetchConfigs, fetchBasicDataDisp, filters.data]);

  const navigate = useNavigate();
  // Render results

  const results = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    if (!DIRECTORY.directoryHandle) {
      return (
        <Card>
          <Button onClick={openDirectory}>
            <FormattedMessage id="husqiotbus.btn_select_config_bus_folder" />
          </Button>
        </Card>
      );
    }
    // route to add configuration page.
    const routeChange = () => {
      dispatch(resetSelectedService());
      navigate(`addconfiguration`);
    };

    return (
      <div>
        <Card>
          <Heading type={HeadingType.h3} uppercase>
            <FormattedMessage id="search.title" />
          </Heading>
          <SearchField
            onSearch={onSearch}
            placeholder={intl.formatMessage({
              id: 'husqiotbus.search_placeholder',
            })}
            disable={isLoading}
          />
        </Card>
        <Card>
          <ButtonGrid>
            <Button onClick={onGenerateXml} marginRight>
              <FormattedMessage id="husqiotbus.btn_generate_xml" />
            </Button>
            <Button onClick={routeChange}>
              <FormattedMessage id="husqiotbus.btn_add_config" />
            </Button>
          </ButtonGrid>
          <CardHeader title="husqiotbus.table_title" />
          {services && services.length > 0 && (
            <ConfigTable services={services} />
          )}
          <Pagination
            noItems={services.length}
            totalCount={totalCount || 0}
            pagination={pagination}
            setPagination={onSetPagination}
          />
        </Card>
      </div>
    );
  }, [
    services,
    isLoading,
    pagination,
    totalCount,
    onSetPagination,
    intl,
    onSearch,
    onGenerateXml,
    dispatch,
    navigate,
    openDirectory,
  ]);

  return (
    <PageWrapper>
      <Heading>
        <FormattedMessage id="husqiotbus.title" />
      </Heading>
      <SubscriptionCard title="subscriptions" />
      <Heading>
        <FormattedMessage id="husqiotbus.configurations" />
      </Heading>
      {results}
    </PageWrapper>
  );
};

export default HusqiotbusStartPage;
